import * as React from 'react';
import { useLocation } from 'react-router-dom';
import Sheet from '@mui/joy/Sheet';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import Drawer from '@mui/joy/Drawer';
import ModalClose from '@mui/joy/ModalClose';
import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link } from '@mui/joy';
import ColourSchemeToggle from './ColourSchemeToggle';
import { useUser } from '../../api/user';
import { useAgentFileActions } from '../../context/AgentFileActionsContext.jsx';
import { getAgent } from '../../api/agent';

const solidPages = {
  '/': true,
  '/api': true,
};

function TitleRow({ children }) {
  return (
    <Typography level="title-sm" sx={{ fontWeight: 'lg', mb: 0.5 }}>
      {children}
    </Typography>
  );
}

export default function MobileHeader() {
  const { email, logout } = useUser() || {};
  const path = useLocation();
  const { fileSession, buildApi } = useAgentFileActions();

  const [open, setOpen] = React.useState(false);
  const [agentLabel, setAgentLabel] = React.useState('');
  const selectedAgentId = fileSession?.selectedAgentId || null;
  const buildName = (buildApi?.agentDisplayName || '').trim();

  React.useEffect(() => {
    if (buildName) {
      setAgentLabel(buildName);
      return;
    }
    if (!selectedAgentId) {
      setAgentLabel('');
      return;
    }
    let cancelled = false;
    getAgent(selectedAgentId)
      .then((a) => {
        if (cancelled) return;
        const name = (a?.name || '').trim();
        setAgentLabel(name || selectedAgentId);
      })
      .catch(() => {
        if (!cancelled) setAgentLabel(selectedAgentId);
      });
    return () => {
      cancelled = true;
    };
  }, [buildName, selectedAgentId]);

  const hasAgentLoaded = !!agentLabel?.trim();

  const onLandingLike =
    path?.pathname === '/landing' ||
    path?.pathname === '/login' ||
    path?.pathname === '/signup' ||
    path?.pathname === '/password-reset';

  if (onLandingLike) return null;

  return (
    <>
      <Sheet
        sx={{
          display: { xs: 'flex', sm: 'none' },
          alignItems: 'center',
          position: 'fixed',
          top: 0,
          width: '100vw',
          height: 'var(--Header-height)',
          zIndex: 9996,
          py: 1,
          px: 1,
          gap: 1,
          backgroundColor: !solidPages[path.pathname] && 'transparent',
          boxShadow: !!solidPages[path.pathname] === '/' && 'sm',
        }}
      >
        <IconButton
          variant="plain"
          color="neutral"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <CloseIcon /> : <MenuIcon />}
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
          {path?.pathname === '/' ? (
            hasAgentLoaded ? (
              <Typography
                level="body-sm"
                fontWeight="md"
                noWrap
                sx={{ maxWidth: 260, textAlign: 'center' }}
                title={agentLabel || undefined}
              >
                {agentLabel}
              </Typography>
            ) : (
              <Link href="/api" aria-label="Open API docs">
                <img src="/logo.png" height={28} alt="Aplisay logo" />
              </Link>
            )
          ) : (
            <Link href="/" aria-label="Home">
              <img src="/logo.png" height={28} alt="Aplisay logo" />
            </Link>
          )}
        </Box>

        <ColourSchemeToggle />
      </Sheet>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        anchor="left"
        size="sm"
        sx={{ '--Drawer-horizontalSize': 'min(360px, 92vw)' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography level="title-lg">Menu</Typography>
            <ModalClose onClick={() => setOpen(false)} />
          </Box>

          <Divider />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TitleRow>Account</TitleRow>
            {email ? (
              <Typography level="body-sm" color="neutral">
                Signed in as {email}
              </Typography>
            ) : (
              <Typography level="body-sm" color="neutral">
                Not signed in
              </Typography>
            )}
            <Button
              size="sm"
              variant="solid"
              color="danger"
              startDecorator={<LogoutIcon />}
              onClick={async () => {
                setOpen(false);
                await logout?.();
              }}
              disabled={!logout}
            >
              Logout
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
